/**
 * Hook: useEvent
 * Manages event CRUD operations
 */

import { useState, useCallback } from 'react';
import {
  createEvent,
  updateEvent,
  deleteEvent,
  transitionEventStatus as transitionStatusRepo,
  uploadEventFlyer as uploadFlyerRepo,
  deleteEventFlyer as removeFlyerRepo,
  uploadEventPaymentQr as uploadPaymentQrRepo,
  deleteEventPaymentQr as removePaymentQrRepo,
} from '@/data/eventRepository';
import { useToast } from '@/hooks/use-toast';
import type { Event, EventStatus } from '@/types/event.types';
import type { Database } from '@/types/supabase';

type EventInsert = Database['public']['Tables']['events']['Insert'];
type EventUpdate = Database['public']['Tables']['events']['Update'];

export function useEvent() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Create event
  const handleCreate = useCallback(
    async (data: EventInsert, options?: { silent?: boolean }): Promise<Event | null> => {
      setIsSubmitting(true);
      try {
        const event = await createEvent(data);
        // silent: the caller shows the success toast after its follow-up work (file uploads)
        if (!options?.silent) {
          toast({
            title: 'Evento creado',
            description: `El evento "${data.name}" fue creado exitosamente.`,
          });
        }
        return event;
      } catch (error) {
        console.error('Error creating event:', error);
        toast({
          variant: 'destructive',
          title: 'Error al crear evento',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [toast],
  );

  // Update event
  const handleUpdate = useCallback(
    async (id: string, data: EventUpdate, options?: { silent?: boolean }): Promise<Event | null> => {
      setIsSubmitting(true);
      try {
        const event = await updateEvent(id, data);
        if (!options?.silent) {
          toast({
            title: 'Evento actualizado',
            description: 'Los cambios fueron guardados exitosamente.',
          });
        }
        return event;
      } catch (error) {
        console.error('Error updating event:', error);
        toast({
          variant: 'destructive',
          title: 'Error al actualizar evento',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [toast],
  );

  // Delete event
  const handleDelete = useCallback(
    async (id: string): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const success = await deleteEvent(id);
        if (success) {
          toast({
            title: 'Evento eliminado',
            description: 'El evento fue eliminado exitosamente.',
          });
        }
        return success;
      } catch (error) {
        console.error('Error deleting event:', error);
        toast({
          variant: 'destructive',
          title: 'Error al eliminar evento',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [toast],
  );

  // Transition event status
  const transitionEventStatus = useCallback(
    async (
      id: string,
      newStatus: EventStatus,
      userId: string,
    ): Promise<Event | null> => {
      setIsSubmitting(true);
      try {
        const event = await transitionStatusRepo(id, newStatus, userId);
        toast({
          title: 'Estado actualizado',
          description: `El evento cambió a estado "${newStatus}".`,
        });
        return event;
      } catch (error) {
        console.error('Error transitioning event status:', error);
        toast({
          variant: 'destructive',
          title: 'Error al cambiar estado',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [toast],
  );

  // Upload flyer
  const uploadFlyer = useCallback(
    async (
      eventId: string,
      file: File,
      userId: string,
    ): Promise<Event | null> => {
      setIsUploading(true);
      try {
        const event = await uploadFlyerRepo(eventId, file, userId);
        toast({
          title: 'Afiche cargado',
          description: 'El afiche fue cargado exitosamente.',
        });
        return event;
      } catch (error) {
        console.error('Error uploading flyer:', error);
        toast({
          variant: 'destructive',
          title: 'Error al cargar afiche',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [toast],
  );

  // Delete flyer
  const deleteFlyer = useCallback(
    async (
      eventId: string,
      filename: string,
      userId: string,
    ): Promise<Event | null> => {
      setIsUploading(true);
      try {
        const event = await removeFlyerRepo(eventId, filename, userId);
        toast({
          title: 'Afiche eliminado',
          description: 'El afiche fue eliminado exitosamente.',
        });
        return event;
      } catch (error) {
        console.error('Error deleting flyer:', error);
        toast({
          variant: 'destructive',
          title: 'Error al eliminar afiche',
          description:
            error instanceof Error
              ? error.message
              : 'Ocurrió un error inesperado.',
        });
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [toast],
  );

  // Bank payment QR (errors surface to the caller; the event itself is already saved)
  const uploadPaymentQr = useCallback(
    async (eventId: string, file: File, userId: string): Promise<Event | null> => {
      try {
        return await uploadPaymentQrRepo(eventId, file, userId);
      } catch (error) {
        console.error('Error uploading payment QR:', error);
        toast({
          variant: 'destructive',
          title: 'Error al cargar el QR de pago',
          description: error instanceof Error ? error.message : 'Ocurrió un error inesperado.',
        });
        return null;
      }
    },
    [toast],
  );

  const deletePaymentQr = useCallback(
    async (eventId: string, filename: string, userId: string): Promise<Event | null> => {
      try {
        return await removePaymentQrRepo(eventId, filename, userId);
      } catch (error) {
        console.error('Error deleting payment QR:', error);
        toast({ variant: 'destructive', title: 'Error al eliminar el QR de pago' });
        return null;
      }
    },
    [toast],
  );

  return {
    uploadPaymentQr,
    deletePaymentQr,
    createEvent: handleCreate,
    updateEvent: handleUpdate,
    deleteEvent: handleDelete,
    transitionEventStatus,
    uploadEventFlyer: uploadFlyer,
    deleteEventFlyer: deleteFlyer,
    isSubmitting,
    isDeleting,
    isUploading,
  };
}
